import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MIN_REASON_LENGTH } from "@amber/contracts";
import { useAppNavigate, useCommandMutation, useCsrf, useToast } from "@amber/ui";
import type {
  Candidate,
  CreateProfileRequest,
  Duplicate,
  IdentityProposal,
  OptionalField,
} from "../../contracts/index";
import { useProfileInvalidation } from "../invalidation";
import { duplicatesQuery, profileCommands, proposalQuery } from "../queries";

type Step = { kind: "find" } | { kind: "proposal"; wikidataId: string | null; url: string | null } | { kind: "manual" };

function useCreateForm() {
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [step, setStep] = useState<Step>({ kind: "find" });
  const [displayName, setDisplayName] = useState("");
  const [languages, setLanguages] = useState<string[]>([]);
  const [rationale, setRationale] = useState("");
  const [role, setRole] = useState("");
  const [different, setDifferent] = useState<Duplicate["id"][]>([]);
  const [accept, setAccept] = useState<Record<OptionalField, boolean>>({
    current_role: true,
    affiliation: true,
    birth_date: true,
    aliases: true,
    photo: true,
  });
  const [completing, setCompleting] = useState<Duplicate | null>(null);
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);
  return {
    text,
    setText,
    query,
    setQuery,
    step,
    setStep,
    displayName,
    setDisplayName,
    languages,
    setLanguages,
    rationale,
    setRationale,
    role,
    setRole,
    different,
    setDifferent,
    accept,
    setAccept,
    completing,
    setCompleting,
    reason,
    setReason,
    submitted,
    setSubmitted,
  };
}

type CreateForm = ReturnType<typeof useCreateForm>;

function proposalInputOf(step: Step) {
  if (step.kind !== "proposal") return null;
  return step.url ? { url: step.url } : { wikidata_id: step.wikidataId ?? "" };
}

function duplicatesOf(
  step: Step,
  proposal: IdentityProposal | undefined,
  manual: { duplicates: Duplicate[] } | undefined,
): Duplicate[] {
  if (step.kind === "proposal") return proposal?.duplicates ?? [];
  if (step.kind === "manual") return manual?.duplicates ?? [];
  return [];
}

function createRequest(f: CreateForm, proposal: IdentityProposal | undefined): CreateProfileRequest {
  if (f.step.kind === "proposal" && proposal) {
    return {
      mode: "wikipedia",
      wikidata_id: proposal.wikidata_id,
      source_url: proposal.source.url,
      display_name: f.displayName.trim() || proposal.name,
      languages: f.languages,
      accept: f.accept,
      different_from: f.different,
      ...(f.rationale.trim() ? { rationale: f.rationale.trim() } : {}),
    };
  }
  return {
    mode: "manual",
    display_name: f.displayName.trim(),
    languages: f.languages,
    rationale: f.rationale.trim(),
    different_from: f.different,
    ...(f.role.trim() ? { current_role: f.role.trim() } : {}),
  };
}

function blockedOf(f: CreateForm, undecided: readonly Duplicate[], proposal: IdentityProposal | undefined) {
  if (f.step.kind === "find") return "Choose a candidate or create the profile manually";
  if (undecided.length > 0 && !f.completing) return "Decide on the possible duplicate first";
  if (f.step.kind === "proposal" && !proposal) return "Reading Wikipedia…";
  return null;
}

function useCreateCommands(f: CreateForm, proposal: IdentityProposal | undefined) {
  const toast = useToast();
  const navigate = useAppNavigate();
  const invalidation = useProfileInvalidation();
  const create = useCommandMutation({
    run: (token) => profileCommands.create(createRequest(f, proposal), token),
    ...invalidation,
    onSuccess: (created) => {
      toast.show("Draft created · finding sources");
      navigate(`/profiles/${created.id}`);
    },
  });
  const complete = useCommandMutation({
    run: (token) => {
      if (!f.completing || !proposal) throw new Error("Nothing to complete");
      return profileCommands.completeIdentity(
        f.completing.id,
        {
          wikidata_id: proposal.wikidata_id,
          source_url: proposal.source.url,
          base_revision: f.completing.revision,
          accept: f.accept,
          reason: f.reason.trim(),
        },
        token,
      );
    },
    ...invalidation,
    onSuccess: () => {
      toast.show(`${f.completing?.name ?? "Draft"} now has its Wikipedia identity · finding sources`);
      navigate(`/profiles/${f.completing?.id ?? ""}`);
    },
  });
  return { create, complete };
}

export function useCreateProfile() {
  const csrf = useCsrf();
  const f = useCreateForm();
  const proposalInput = proposalInputOf(f.step);
  const proposal = useQuery({
    ...proposalQuery(proposalInput ?? {}, csrf()),
    enabled: proposalInput !== null,
  });
  const manualDuplicates = useQuery({
    ...duplicatesQuery(f.displayName.trim()),
    enabled: f.step.kind === "manual" && f.displayName.trim().length > 1,
  });
  const duplicates = duplicatesOf(f.step, proposal.data, manualDuplicates.data);
  const undecided = duplicates.filter((d) => !f.different.includes(d.id));
  const { create, complete } = useCreateCommands(f, proposal.data);
  const choose = (c: Candidate) => {
    f.setStep({ kind: "proposal", wikidataId: c.wikidata_id, url: c.article_url });
    f.setDisplayName(c.name);
    f.setLanguages(c.edition === "de" ? ["de"] : ["en"]);
    f.setDifferent([]);
    f.setCompleting(null);
  };
  const find = () => {
    const q = f.text.trim();
    f.setQuery(q);
    f.setStep({ kind: "find" });
  };
  const submit = () => {
    f.setSubmitted(true);
    if (f.completing) {
      if (f.reason.trim().length >= MIN_REASON_LENGTH) complete.mutate();
      return;
    }
    if (
      f.languages.length === 0 ||
      (f.step.kind === "manual" && (f.rationale.trim().length === 0 || f.displayName.trim().length === 0))
    )
      return;
    create.mutate();
  };
  return {
    ...f,
    proposal,
    duplicates,
    choose,
    find,
    submit,
    languagesInvalid: f.submitted && f.languages.length === 0,
    rationaleInvalid: f.submitted && f.step.kind === "manual" && f.rationale.trim().length === 0,
    reasonInvalid: f.submitted && f.completing !== null && f.reason.trim().length < MIN_REASON_LENGTH,
    blocked: blockedOf(f, undecided, proposal.data),
    busy: create.isPending || complete.isPending,
    error: create.error ?? complete.error,
  };
}

export type CreateFlow = ReturnType<typeof useCreateProfile>;
